import { DownloadOutlined, DownOutlined, LoadingOutlined, SwapOutlined } from "@ant-design/icons"
import { Button, Dropdown } from "antd"
import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { DownloadTray } from "../../../../Icon/icons/download-tray.tsx"
import { ExportOut } from "../../../../Icon/icons/export-out.tsx"
import { UploadTray } from "../../../../Icon/icons/upload-tray.tsx"
import { showErrorNotifications } from "../../../../utils/handlers/handle-error"
import { DocumentCreateMenuOption } from "../../../Lead/LeadWorkspacePage/documents/DocumentCreateMenuOption.tsx"

const XLSX_ACCEPT =
  ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

interface LeadsImportExportMenuProps {
  /** `lead_create` — gates the import half of the menu. */
  canImport: boolean
  onImport: (file: File) => void | Promise<void>
  onDownloadTemplate: () => Promise<void>
  onExport: () => Promise<void>
  /** An export already running (e.g. started from a table selection). */
  exporting?: boolean
}

export function LeadsImportExportMenu({
  canImport,
  onImport,
  onDownloadTemplate,
  onExport,
  exporting
}: LeadsImportExportMenuProps) {
  const { t } = useTranslation()

  const [menuOpen, setMenuOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const running = busy || !!exporting

  const runBusy = async (fn: () => void | Promise<void>) => {
    setBusy(true)
    try {
      await fn()
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setBusy(false)
      setMenuOpen(false)
    }
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const isXlsx =
      file.name.endsWith(".xlsx") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

    if (!isXlsx) {
      showErrorNotifications(t("leadsTable.actions.importInvalidFile"))
    } else {
      void runBusy(() => onImport(file))
    }

    e.target.value = ""
  }

  if (!canImport) {
    return (
      <Button
        icon={<DownloadOutlined />}
        loading={running}
        onClick={() => void runBusy(onExport)}
      >
        {t("leadsTable.actions.exportLeads")}
      </Button>
    )
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type={"file"}
        accept={XLSX_ACCEPT}
        className={"hidden"}
        onChange={onFileChange}
      />

      <Dropdown
        trigger={["click"]}
        open={menuOpen}
        onOpenChange={setMenuOpen}
        popupRender={() => (
          <div
            className={
              "flex w-96 flex-col gap-1 rounded-card border border-border bg-white p-2 shadow-lg"
            }
          >
            <DocumentCreateMenuOption
              icon={running ? <LoadingOutlined /> : <UploadTray />}
              accent={"indigo"}
              title={t("leadsTable.actions.importLeads")}
              description={t("leadsTable.actions.importLeadsDescription")}
              disabled={running}
              onClick={() => fileInputRef.current?.click()}
            />
            <DocumentCreateMenuOption
              icon={running ? <LoadingOutlined /> : <DownloadTray />}
              accent={"orange"}
              title={t("leadsTable.actions.downloadTemplate")}
              description={t("leadsTable.actions.downloadTemplateDescription")}
              disabled={running}
              onClick={() => void runBusy(onDownloadTemplate)}
            />

            <div className={"mx-1 my-1 border-t border-border"} />

            <DocumentCreateMenuOption
              icon={running ? <LoadingOutlined /> : <ExportOut />}
              accent={"green"}
              title={t("leadsTable.actions.exportLeads")}
              description={t("leadsTable.actions.exportLeadsDescription")}
              disabled={running}
              onClick={() => void runBusy(onExport)}
            />
          </div>
        )}
      >
        <Button type={"primary"} className={"w-auto"}>
          <span className={"inline-flex items-center gap-1.5"}>
            <SwapOutlined />
            {t("leadsTable.actions.importExport")}
            <span
              aria-hidden
              className={"mx-1 h-4 w-px bg-current opacity-30"}
            />
            <DownOutlined className={"text-xs"} />
          </span>
        </Button>
      </Dropdown>
    </>
  )
}
